import platform from 'platform';
import { useContext, useEffect, useRef, useState } from 'react';

import { ErrorBoundary } from '@sentry/react';
import { GoogleMapsContext } from 'components/context/GoogleMapsProvider';
import TripPlanScreen from 'components/TripScreen/TripPlanScreen/TripPlanScreen';
import TripSearchScreen from 'components/TripScreen/TripSearchScreen/TripSearchScreen';
import { useSendWithLoaderConfig, useShortCode } from '../../context/loaderContext';

// import analytics from 'utils/analytics';
import config from 'config';
import lokalise from 'utils/lokalise';
import userstore from 'utils/userstore';

function TripScreen({
    appLocation,
    params,

    isPermanent,

    showTripScreen,
    tripScreenLocation,

    toggleTripScreen,
}) {

    const eleTripScreen = useRef();
    const eleTripPlanScreen = useRef();
    const sendWithLoaderConfig = useSendWithLoaderConfig();
    const shortCode = useShortCode();

    const [tripOptions, setTripOptions] = useState(null);
    const [selectedTripPlan, setTripPlan] = useState(null);
    const [showTripPlanScreen, setTripPlanScreen] = useState(null);
    const [searchingTrips, setSearchingTrips] = useState(false);

    const [statusMessage, setStatusMessage] = useState(null);

    const [startingLocation, setStartingLocation] = useState(tripScreenLocation || null);// {coords: {latitude, longitude}}
    const [endingLocation, setEndingLocation] = useState(null);// {coords: {latitude, longitude}}
    const [startDate, setStartDate] = useState(null);
    const [startTime, setStartTime] = useState(null);

    const [loading, setLoading] = useState(false);
    const [errorCount, setErrorCount] = useState(0);
    const [disableInput, setDisableInput] = useState(shortCode === 'R5BD9' ? 'bottom' : null);
    const gmaps = useContext(GoogleMapsContext);
    const queryParams = new URLSearchParams(url.search);
    const pgaLocations = shortCode === 'R5BD9' ? [ 
        {
            coords: {
                latitude: 40.736615,
                longitude: -73.463623,
            },
            googlePlaceId: 'mockId-afpga1',
            name: '2025 Ryder Cup via Rideshare'
        },
        {
            coords: {
                latitude: 40.738958,
                longitude: -73.460156
            },
            googlePlaceId: 'mockId-afpga2',
            name: '2025 Ryder Cup Shuttle via Transit'
        },{
            coords: {
                latitude: 40.738958,
                longitude: -73.460156
            },
            googlePlaceId: 'mockId-afpga3',
            name: '2025 Ryder Cup Shuttle via Jones Beach'
        }
    ] : null;

    async function coordinatesSearch(inputValue, location) {
        return await gmaps.getGMPlacesResults(inputValue, appLocation || location, queryParams.has('lang') ? queryParams.get('lang') : 'en')
            .then((newResults) => {

                return newResults[0];
            })
        .catch((e) => { });
    }

    function isCoordinates(str) {
        if (typeof str !== 'string') return false;
      
        const parts = str.split(',').map(s => s.trim());
      
        if (parts.length !== 2) return false;
      
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
      
        if (isNaN(lat) || isNaN(lng)) return false;
      
        return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
      }

    useEffect(async () => {
        // On first load, 
        const routing = await userstore.getUserRouting();
        // console.log("Saved Routing", routing);
        if (routing && routing.start) {
            // Check if just coordinates and if so, update to be a proper address
            if (isCoordinates(routing.start.name)) {
                const address = await coordinatesSearch(routing.start.name, startingLocation);
                setStartingLocation({
                    ...routing.start,
                    name: address?.name
                });
            }
            else {
                setStartingLocation(routing.start);
            }
        }

        if (routing && routing.end) {
            
            // Check if just coordinates and if so, update to be a proper address
            if (isCoordinates(routing.end.name)) {
                const address = await coordinatesSearch(routing.end.name, endingLocation);
                setEndingLocation({
                    ...routing.end,
                    name: address.name
                });
            }
            else {
                setEndingLocation(routing.end);
            }
            // if (true) {
            if (shortCode === 'R5BD9') {
                // For PGA, we need to set the ending location to the PGA location
                setEndingLocation(pgaLocations ? pgaLocations[0] : {
                    coords: {
                        latitude: 40.736615,
                        longitude: -73.463623,
                    },
                    name: '2025 Ryder Cup via Rideshare'
                });
            }
        }

        if (routing && routing.startDate) {
            setStartDate(routing.startDate);
        }
        if (routing && routing.startTime) {
            setStartTime(routing.startTime);
        }
    }, []);

    // We allow for forced setting of the start location to accommodate CardScreen requests
    useEffect(() => {
        if (eleTripScreen.current && tripScreenLocation) {
            setStartingLocation(tripScreenLocation);
        }
    }, [tripScreenLocation]);

    useEffect(() => {
        // Toggles off the trip plan screen if this comp is asked to be hidden
        if (showTripScreen === false) {
            setTripPlanScreen(false);
        }
    }, [showTripScreen])

    // This is necessary to persist input fields next time user reloads the page
    useEffect(() => {
        updateSavedRouting();
    }, [startingLocation, endingLocation, startDate, startTime]);

    function updateSavedRouting() {
        userstore.setUserRouting(startingLocation, endingLocation, startDate, startTime);
    }

    // Converting nameless locations "37,-93" only happens for URL param start/end or user geolocation
    function setNiceNameForLocation(location, setLocationMethod) {
        gmaps.getGMPlacesLocation(location).then((parsedLocation) => {
            if (parsedLocation) {
                // console.log("parsedLocation", parsedLocation);
                setLocationMethod(parsedLocation);
            }
        });
    }

    function formatLocationForDataDrop(location) {
        return {
            address: location.address,
            latitude: Number(location.coords.latitude).toFixed(3) * 1,
            longitude: Number(location.coords.longitude).toFixed(3) * 1,
            name: location.name,
        }
    }

    function setStartingLocationEvent(location) {
        sendWithLoaderConfig(formatLocationForDataDrop(location), 'select-starting-location')
        processLocation(location, setStartingLocation);
    }

    function setEndingLocationEvent(location) {
        sendWithLoaderConfig(formatLocationForDataDrop(location), 'select-ending-location')
        processLocation(location, setEndingLocation);
    }

    function processLocation(location, setLocationMethod) {
        if (location.type == 'usergeolocation') {
            setNiceNameForLocation(location, setLocationMethod);
        } else {
            setLocationMethod(location);
        }
    }

    function formatTripDateTime(date, time) {
        const [hour, minute] = time.split(':');
        const year = date.slice(0, 4)
        const month = date.slice(4, 6);
        const day = date.slice(6, 8);
        const today = new Date();
        today.setFullYear(year);
        //TODO: Figure out why it is adding one when using setMonth()
        today.setMonth(month - 1);
        today.setDate(day);
        today.setHours(hour);
        today.setMinutes(minute);
        return new Date(today.getTime() - today.getTimezoneOffset() * 60000).toISOString().slice(0, -5);
    }

    function removeDuplicateJourney(options) {
        // Routing may return multiple routings for each mode (ie: different driving paths)
        // Right now we will only offer one of each
        let existingOptions = [];
        return options.map((option) => {
            if (existingOptions.includes(option.name)) {
                return null
            }
            existingOptions.push(option.name);
            return option
        }).filter((option) => option)
    }

    async function getTripPlan(date, time) {
        //Start searching
        setSearchingTrips(true);
        setTripOptions(null);

        // console.log(date, time, startingLocation, endingLocation);
        if (!date) {
            setStatusMessage(lokalise.getKey("ts12o"));
            setLoading(false);
            return;
        }

        if (!time) {
            setStatusMessage(lokalise.getKey("ts12p"));
            setLoading(false);
            return;
        }

        if (!startingLocation || !startingLocation.coords || !endingLocation || !endingLocation.coords) {
            setStatusMessage(lokalise.getKey("ts12q"));
            setLoading(false);
            return;
        }

        const data = await fetch(config.routingBackendURL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                apiKey: config.routingBackendKey,
                // Temporary measure for PGA
                // agencySlugs: params.key === 'BNgEjzn9T2Sy7c1y1Iknvc701PPYNhGajgtxT5bHbLAl3YALbG45ItoaKBNu98Mg' ? 'tms-pga-shuttle-gtfs' : null,
                // agencySlugs: params.key === 'GqwQyJHbHdNoSQG6TeMNSr9cxw7UWf5VE2CiGDIect2lQVzTicGW5vhU21AHX0v7' ? 'tms-pga-shuttle-gtfs' : null,
                
                fromCoordinates: {
                    latitude: startingLocation.coords.latitude,
                    longitude: startingLocation.coords.longitude
                },
                toCoordinates: {
                    latitude: endingLocation.coords.latitude,
                    longitude: endingLocation.coords.longitude
                },
                departBy: formatTripDateTime(date, time), 
                locationCode: params.locationCode,
                customerKey: params.key,
                metadata: {
                    url: window.location.href,
                    appName: 'Citymotion',
                    appVersion: config.version,
                    browser: platform.name,
                    browserVersion: platform.version,
                    os: platform.os.family,
                    osVersion: platform.os.version,
                    device: platform.product,
                    isoDate: new Date().toISOString(),
                },
            })
        })
            .then((response) => response.json())
            .then((json) => {
                let journey = json?.data?.journey;
                if (journey && journey.length > 0) {
                    // if (params.key === 'BNgEjzn9T2Sy7c1y1Iknvc701PPYNhGajgtxT5bHbLAl3YALbG45ItoaKBNu98Mg') {
                    if (shortCode === 'R5BD9') {
                        const drive = journey.find(item => item.category === 'Drive' && item.name === 'Car');
                        journey = (journey || []).filter(
                            item => {
                                // Only show modes depending on selected dropdown
                                if (item.category === 'RideHailing' && endingLocation.name === '2025 Ryder Cup via Rideshare') return item;
                                if (item.category === 'Shuttle' && endingLocation.name === '2025 Ryder Cup Shuttle via Transit') return item;
                                if (item.name === 'Park and Ride' && endingLocation.name === '2025 Ryder Cup Shuttle via Jones Beach') return item;
                                return;
                            }
                        );
                        let rideHailing = journey.find(item => item.category === 'RideHailing');
                        // Set up ridehailing as if it is drive mode
                        if (rideHailing) {
                            const tempDriveLeg = drive?.legs ? drive.legs[0] : null;
                            rideHailing = {
                                ...rideHailing,
                                legs: rideHailing.legs.map((leg, idx) =>
                                    idx === 1 && tempDriveLeg
                                        ? {
                                            ...tempDriveLeg,
                                            ...leg, // leg props take precedence if they exist
                                        }
                                        : leg
                                ),
                                scores: drive?.scores
                            };
                            journey = journey.map(item =>
                                item.category === 'RideHailing' ? rideHailing : item
                            );
                        }

                    }
                    setTripOptions(removeDuplicateJourney(journey));
                    setStatusMessage("");
                } else if (json.message) {
                    setStatusMessage(`Please try a different location (${json.message})`);
                } else {
                    setStatusMessage(lokalise.getKey("ts12r"));
                }
                setLoading(false);
                setErrorCount(0);
            })
            .catch((error) => {
                setErrorCount(errorCount + 1);
                if (error.toString().includes('TypeError: Failed to fetch')) {
                    // Usually CORS problem
                    setStatusMessage(lokalise.getKey("ts12s"));

                } else {
                    setStatusMessage(`${lokalise.getKey("ts12t")} ` + JSON.stringify(error));
                }
                if (errorCount > 3) {
                    setStatusMessage(lokalise.getKey("ts12u"));
                }
                setLoading(false);
                console.error('Something went wrong:', error);
            });

        //End search
        setSearchingTrips(false);
    }

    function onClickStatusMessage() {
        if (errorCount > 3) {
            // @TODO Mailto Actionfigure.ai
        }
    }

    function formatTripPlanForDataDrop(plan) {
        // Taking out geometry coordinates to reduce size
        const { geometryCoordinates, ...planWithoutGeometry } = plan;
        return {
            ...planWithoutGeometry,
            legs: planWithoutGeometry.legs.map((leg) => {
                return {
                    ...leg,
                    directions: leg.directions ? leg.directions.map(direction => {
                        return {
                            ...direction,
                            coordinates: {
                                latitude: Number(direction.coordinates.latitude).toFixed(3) * 1,
                                longitude: Number(direction.coordinates.longitude).toFixed(3) * 1,
                            }
                        }
                    }) : null
                }
            }),
            startingLocation: {
                latitude: Number(startingLocation.coords.latitude).toFixed(3) * 1,
                longitude: Number(startingLocation.coords.longitude).toFixed(3) * 1,
                country: startingLocation.parsedAddress?.country,
                place: startingLocation.parsedAddress?.place,
                postcode: startingLocation.parsedAddress?.postcode,
                region: startingLocation.parsedAddress?.region
            },
            endingLocation: {
                latitude: Number(endingLocation.coords.latitude).toFixed(3) * 1,
                longitude: Number(endingLocation.coords.longitude).toFixed(3) * 1,
                country: endingLocation.parsedAddress?.country,
                place: endingLocation.parsedAddress?.place,
                postcode: endingLocation.parsedAddress?.postcode,
                region: endingLocation.parsedAddress?.region
            },
            url_params: params
        }
    }

    async function selectTripOption(plan) {
        setTripPlan(plan);
        // analytics.postToDataDrop(formatTripPlanForDataDrop(plan), 'trip-option', params);
        sendWithLoaderConfig(formatTripPlanForDataDrop(plan), 'trip-option')

        setTripPlanScreen(true);
    }

    function switchLocation() {
        const temp = {...startingLocation, address: startingLocation?.name};
        setStartingLocation({...endingLocation, address: endingLocation?.name});
        setEndingLocation(temp);
        if (disableInput) {
            setDisableInput(disableInput === 'bottom' ? 'top' : 'bottom');
        }
    }

    useEffect(() => {
        if (showTripScreen || isPermanent) {

            if (isPermanent) {
                eleTripScreen.current.style.transition = 'none';
            }

            // Open screen
            requestAnimationFrame(() => {
                if (eleTripScreen && eleTripScreen.current) {
                    eleTripScreen.current.style.display = 'block';
                    setTimeout(() => { // Ready browser for painting
                        requestAnimationFrame(() => {
                            eleTripScreen.current.style.bottom = 0;
                        });
                    }, 1);
                }
            });
        } else if (!showTripScreen && !isPermanent) {
            // Close screen
            requestAnimationFrame(() => {
                if (eleTripScreen && eleTripScreen.current) {
                    eleTripScreen.current.style.bottom = '-100vh';
                    setTimeout(() => {
                        eleTripScreen.current.style.display = 'none';
                    }, 275);
                }
            });
        }
    }, [showTripScreen])


    useEffect(() => {
        requestAnimationFrame(() => {
            if (eleTripPlanScreen && eleTripPlanScreen.current) {
                if (showTripPlanScreen) {
                    eleTripPlanScreen.current.style.left = 0;
                } else {
                    // Add some extra vw for box-shadow
                    eleTripPlanScreen.current.style.left = '130vw';
                }
            }
        })
    }, [showTripPlanScreen]);

    return (
        <div ref={eleTripScreen} className="TripScreen">

            <div className="screen-search">
                <ErrorBoundary>
                    <TripSearchScreen
                        // Only necessary non-TripScreen props
                        toggleTripScreen={toggleTripScreen}
                        isPermanent={isPermanent}
                        searchingTrips={searchingTrips}

                        // State 
                        statusMessage={statusMessage}
                        tripOptions={tripOptions}
                        setTripOptions={setTripOptions}
                        showTripPlanScreen={showTripPlanScreen}
                        errorCount={errorCount}

                        // Location objects
                        startingLocation={startingLocation}
                        endingLocation={endingLocation}
                        startDate={startDate}
                        startTime={startTime}

                        // Methods and Setters
                        getTripPlan={getTripPlan}
                        setStartingLocation={setStartingLocationEvent}
                        setEndingLocation={setEndingLocationEvent}
                        setStartDate={setStartDate}
                        setStartTime={setStartTime}
                        loading={loading}
                        setLoading={setLoading}
                        switchLocation={switchLocation}

                        searchAreaLocation={appLocation || startingLocation}

                        selectTripOption={selectTripOption}
                        onClickStatusMessage={onClickStatusMessage}

                        shortCode={shortCode}
                        pgaLocations={pgaLocations}
                        disableInput={disableInput}

                        params={params}
                    />
                </ErrorBoundary>
            </div>

            <div ref={eleTripPlanScreen} className="screen-trip-plan">
                <ErrorBoundary>
                    <TripPlanScreen
                        // State
                        tripOptions={tripOptions}
                        selectedTripPlan={selectedTripPlan}

                        // Location objs
                        startingLocation={startingLocation}
                        endingLocation={endingLocation}

                        // Methods and setters
                        showTripPlanScreen={showTripPlanScreen}
                        setTripPlanScreen={setTripPlanScreen}

                        params={params}
                    />
                </ErrorBoundary>
            </div>
        </div>
    )
}


export default TripScreen
